/*
  Shared tracking-event vocabulary + reusable viewport observer for RampJS.

  Historically each ad-type path built its own pingback URLs and decided its own
  event set: the Google RSoC path (google-rsoc.ts) fired the rich diagnostic set
  (mutation_observed, resize_observed, timeout, page_exit), Google CSA
  (modules/google-csa) fired ad-loaded + a click heuristic, and the injectable
  units (injectable-units.ts — Yahoo / dynamic_creative / embedded_serp) fired
  only impression + a relayed click. There was no shared event vocabulary and no
  viewport ("in view") signal anywhere.

  This module is the standardization seam (ABE-864): one canonical set of event
  kinds every path can reference, and one reusable fire-once IntersectionObserver
  so any component RampJS draws can report when its slot actually entered the
  viewport. Each path still owns its own URL/wire format — RSoC/CSA encode events
  as adLoadedCallback query params (e.g. `in_view=true`, a sibling of the
  existing `mutation_observed=true`), while injectable units reuse their
  server-supplied impression URL with an `event=in_view` marker — but the event
  *values* now come from here so the vocabulary stays consistent.
*/

/**
 * Canonical RampJS tracking-event kinds. These are the standardized signals an
 * ad-type path can emit; add a new kind here so every path shares one
 * vocabulary rather than re-deriving inline string literals.
 *
 *  - `impression`        — the unit was placed / an ad was returned
 *  - `click`             — the user clicked into the ad
 *  - `mutation_observed` — an ad iframe was injected into the container
 *  - `resize_observed`   — the container changed size after initial layout
 *  - `in_view`           — the ad slot entered the viewport (fire-once)
 */
export const TrackingEvent = {
  Impression: "impression",
  Click: "click",
  MutationObserved: "mutation_observed",
  ResizeObserved: "resize_observed",
  InView: "in_view",
} as const;

export type TrackingEvent = (typeof TrackingEvent)[keyof typeof TrackingEvent];

/**
 * Query-param marker key used when an event is encoded onto an opaque,
 * server-supplied beacon URL (the injectable-unit path). RSoC/CSA instead use
 * discrete boolean param keys keyed by the event value itself
 * (`in_view=true`), matching their existing `mutation_observed`/
 * `resize_observed` wire contract.
 */
export const EVENT_PARAM = "event";

/**
 * Fraction of an ad slot that must be within the viewport before we count it as
 * viewed and fire `in_view`. Viewability-style threshold — tune here; it is the
 * single knob shared by every ad-type path (and mirrored in the renderer's
 * embedded-SERP runtime, which cannot import it).
 */
export const IN_VIEW_THRESHOLD = 0.5;

/**
 * Observe `target` and invoke `onInView` once — the first time at least
 * {@link IN_VIEW_THRESHOLD} of it is within the viewport. Returns a disconnect
 * function.
 *
 * A unit already ≥50%-visible at page load fires immediately (IntersectionObserver
 * delivers an initial callback for a visible target); a below-the-fold unit fires
 * when scrolled to ≥50%. A unit positioned in view but still collapsed (0px,
 * because the ad has not rendered yet) fires the moment it renders — growing from
 * zero area to full crosses the 0.5 threshold, which triggers a fresh callback,
 * so no scroll is needed. (Note: a unit taller than the viewport may never reach
 * 50% of its own area on screen and so may never fire — inherent to a
 * fraction-of-the-unit threshold.)
 *
 * Fire-once: the observer disconnects itself after firing, so `onInView` runs
 * at most once. If `IntersectionObserver` is unavailable (older browsers, some
 * embedded webviews) this is a no-op returning a no-op disconnect — the caller
 * simply never receives an in_view signal, the intended "may never fire".
 *
 * Lifecycle note: in_view happens whenever the user scrolls the slot on screen,
 * which for a below-the-fold unit can be long after the ad has (or hasn't)
 * loaded. Callers should NOT tear this observer down on an ad-load timeout —
 * only on page exit, if at all. It self-disconnects on fire and is GC'd with
 * its target otherwise.
 *
 * @param target  Element to watch (an ad container, or an injected iframe).
 * @param onInView Called at most once when `target` first reaches the threshold.
 * @param options IntersectionObserver options (rootMargin, root, …); `threshold`
 *                is forced to {@link IN_VIEW_THRESHOLD}.
 */
export function observeInView(
  target: Element,
  onInView: () => void,
  options?: IntersectionObserverInit,
): () => void {
  if (typeof IntersectionObserver === "undefined") {
    return () => {};
  }

  let fired = false;
  const observer = new IntersectionObserver(
    (entries) => {
      if (fired) return;
      for (const entry of entries) {
        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= IN_VIEW_THRESHOLD
        ) {
          fired = true;
          observer.disconnect();
          onInView();
          return;
        }
      }
    },
    { ...options, threshold: IN_VIEW_THRESHOLD },
  );

  observer.observe(target);
  return () => observer.disconnect();
}

/**
 * Wire the ad-load ground-truth container observers that were previously
 * RSoC-only, so any container-based ad path (RSoC, CSA) reports the same signals:
 *
 *  - `mutation_observed` — the first `<iframe>` is injected into the container
 *    (or any descendant), i.e. the ad system rendered something.
 *  - `resize_observed`   — the container gained real (non-zero) dimensions that
 *    changed from its initial layout — ground truth that ad content rendered
 *    and affected the container size. The initial observe callback is skipped.
 *
 * Each event fires at most once via `onEvent(event)`, after which that observer
 * disconnects. Returns a handle whose `disconnect()` tears down any observer
 * still active — call it on the ad-load timeout / page exit.
 *
 * NOTE: `in_view` is deliberately NOT wired here. It is a viewport/scroll signal
 * with a different (page) lifetime — folding it in would let the ad-load timeout
 * tear it down before a below-the-fold slot is ever scrolled into view. Callers
 * wire it separately with `observeInView`.
 *
 * The caller owns URL construction and transport — `onEvent` receives the
 * `TrackingEvent` kind and is responsible for building + firing the pingback —
 * so RSoC (adLoadedCallback + FB diagnostics) and CSA (plain adLoadedCallback)
 * can each encode the event in their own wire format while sharing this logic.
 */
export function observeAdContainer(
  container: Element,
  onEvent: (event: TrackingEvent) => void,
): { disconnect: () => void } {
  const disconnects: Array<() => void> = [];

  // MutationObserver — detect iframe injection.
  if (typeof MutationObserver !== "undefined") {
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          // The ad system may append the iframe directly, or a wrapper element
          // that already contains it (in which case only the wrapper is in
          // addedNodes) — check descendants too so we don't miss the injection.
          if (
            node instanceof HTMLIFrameElement ||
            (node instanceof Element && node.querySelector("iframe") !== null)
          ) {
            mutationObserver.disconnect();
            onEvent(TrackingEvent.MutationObserved);
            return;
          }
        }
      }
    });
    mutationObserver.observe(container, { childList: true, subtree: true });
    disconnects.push(() => mutationObserver.disconnect());
  }

  // ResizeObserver — detect a real, post-layout size change.
  if (typeof ResizeObserver !== "undefined") {
    const initialRect = container.getBoundingClientRect();
    const resizeObserver = new ResizeObserver(() => {
      const currentRect = container.getBoundingClientRect();
      // Wait for a rendered ad with real dimensions on both axes; a change on a
      // single axis while the other is 0 means the unit is still collapsed.
      if (currentRect.width <= 0 || currentRect.height <= 0) return;
      if (
        currentRect.width === initialRect.width &&
        currentRect.height === initialRect.height
      ) {
        return;
      }
      resizeObserver.disconnect();
      onEvent(TrackingEvent.ResizeObserved);
    });
    resizeObserver.observe(container);
    disconnects.push(() => resizeObserver.disconnect());
  }

  return {
    disconnect() {
      for (const disconnect of disconnects) disconnect();
    },
  };
}

/**
 * Append an `event=<kind>` marker to an opaque, server-supplied beacon URL,
 * preserving any existing query string. Used by the injectable-unit path to
 * turn its impression-beacon URL into an in_view pingback without a separate
 * server-supplied view URL (see ABE-864). Falls back to string concatenation
 * for non-absolute URLs so the signal is never silently dropped.
 */
export function withEventMarker(
  beaconUrl: string,
  event: TrackingEvent,
): string {
  try {
    const url = new URL(beaconUrl);
    url.searchParams.set(EVENT_PARAM, event);
    return url.toString();
  } catch {
    // Relative/opaque URL — insert the marker before any #fragment so it stays
    // a query parameter rather than becoming part of the fragment.
    const hashIndex = beaconUrl.indexOf("#");
    const base = hashIndex === -1 ? beaconUrl : beaconUrl.slice(0, hashIndex);
    const fragment = hashIndex === -1 ? "" : beaconUrl.slice(hashIndex);
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}${EVENT_PARAM}=${encodeURIComponent(
      event,
    )}${fragment}`;
  }
}
